/**
 * LoginActivityPage (iter203a) — admin-only view of LOGIN/LOGOUT rows
 * from ActionLog. Answers "who logged in, when, from where, and are they
 * still signed in?" without exposing the full audit log.
 *
 * Backed by GET /api/admin/login-activity which pre-filters to
 * action_type IN ('LOGIN','LOGOUT') + joins against user_sessions so we
 * can render the "still active" badge next to each row.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SignIn, SignOut, ArrowClockwise, MagnifyingGlass, Users } from "@phosphor-icons/react";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";

const SINCE_OPTIONS = [
  { v: 1,  label: "24h" },
  { v: 7,  label: "7d" },
  { v: 30, label: "30d" },
  { v: 90, label: "90d" },
];

const RESULT_OPTIONS = [
  { v: "",        label: "All" },
  { v: "success", label: "Success" },
  { v: "failed",  label: "Failed" },
  { v: "error",   label: "Error" },
];

function fmtTs(utc) {
  if (!utc) return "—";
  try {
    const d = new Date(utc.endsWith?.("Z") ? utc : `${utc}Z`);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false, timeZoneName: "short",
    }).format(d);
  } catch { return String(utc); }
}

const RESULT_BADGE = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed:  "bg-amber-50  text-amber-800  border-amber-300",
  error:   "bg-red-50    text-red-700    border-red-300",
  skipped: "bg-slate-50  text-slate-500  border-slate-200",
};

export default function LoginActivityPage({ user, onLogout, onLogoutAll }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [since, setSince] = useState(30);
  const [result, setResult] = useState("");
  const [search, setSearch] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { since_days: since, limit: 300 };
      if (result) params.result = result;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/admin/login-activity", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load login activity");
    } finally { setLoading(false); }
  }, [since, result, search]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white"
         data-testid="login-activity-page">
      <AdminPageHeader
        icon={<SignIn size={20} weight="fill" className="text-blue-700" />}
        title="Login Activity"
        subtitle="Every sign-in and sign-out — filterable, admin-only"
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
        testId="login-activity-back"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-4">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-sm p-3">
          <div className="flex items-center gap-1" data-testid="login-activity-since-group">
            {SINCE_OPTIONS.map(o => (
              <button key={o.v} onClick={() => setSince(o.v)}
                      data-testid={`login-activity-since-${o.v}`}
                      className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wider border transition
                        ${since === o.v
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                {o.label}
              </button>
            ))}
          </div>
          <div className="h-6 w-px bg-slate-200" />
          <select value={result} onChange={(e) => setResult(e.target.value)}
                  data-testid="login-activity-result"
                  className="h-8 text-xs rounded-sm border border-slate-200 px-2 bg-white">
            {RESULT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} weight="bold"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search}
                   onChange={(e) => setSearch(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && refresh()}
                   placeholder="Search email, label, or IP…"
                   data-testid="login-activity-search"
                   className="w-full h-8 pl-7 pr-2 text-xs rounded-sm border border-slate-200 bg-white" />
          </div>
          <button onClick={refresh} disabled={loading}
                  data-testid="login-activity-refresh"
                  className="h-8 px-3 rounded-sm border text-xs font-bold uppercase tracking-wider
                             bg-white text-slate-700 border-slate-200 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
          <Link to="/admin/sessions"
                data-testid="login-activity-sessions-link"
                className="h-8 px-3 rounded-sm border text-xs font-bold uppercase tracking-wider
                           bg-white text-blue-700 border-blue-200 hover:bg-blue-50 flex items-center gap-1">
            <Users size={12} weight="bold" /> Active Sessions
          </Link>
        </div>

        {/* Stats strip */}
        <div className="text-xs text-slate-500" data-testid="login-activity-count">
          {loading ? "Loading…" : `${total.toLocaleString()} event(s) in the last ${since} day${since === 1 ? "" : "s"}`}
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="login-activity-table">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Method</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">IP</th>
                <th className="text-left px-3 py-2">Result</th>
                <th className="text-left px-3 py-2 hidden lg:table-cell">Active?</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400"
                        data-testid="login-activity-empty">
                  No login events found for these filters.
                </td></tr>
              )}
              {items.map((it, idx) => (
                <tr key={`${it.id || idx}-${it.ts_utc}`}
                    className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-slate-600">
                    {fmtTs(it.ts_utc)}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider
                      ${it.action_type === "LOGIN"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                      {it.action_type === "LOGIN"
                        ? <SignIn size={10} weight="bold" />
                        : <SignOut size={10} weight="bold" />}
                      {it.action_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800">{it.actor_email || "—"}</td>
                  <td className="px-3 py-2 hidden sm:table-cell text-slate-600">{it.method || "—"}</td>
                  <td className="px-3 py-2 hidden md:table-cell font-mono text-[11px] text-slate-500">{it.ip_address || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider
                      ${RESULT_BADGE[it.result] || "bg-slate-50 text-slate-500 border-slate-200"}`}>
                      {it.result || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    {it.has_active_session ? (
                      <span className="inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border-blue-200">
                        Active
                      </span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
